/*!

=========================================================
* Argon Design System React - v1.1.2
=========================================================

* Product Page: https://www.creative-tim.com/product/argon-design-system-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)
* Licensed under MIT (https://github.com/creativetimofficial/argon-design-system-react/blob/master/LICENSE.md)

* Coded by Creative Tim

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

*/
import React from "react";
// nodejs library that concatenates classes

// reactstrap components
import {
  Card,
  CardBody,
  Container,
  Row,
  Col, Button, Modal, Form, FormGroup, InputGroup, InputGroupText, Input, UncontrolledTooltip, Spinner,
} from "reactstrap";

import DebouncedWordCloud from "../../components/helpers/DebouncedWordCloud";

// core components

// index page sections


import {getStats} from "../../components/helpers/StatsHelpers";
import {
  RiAddLine,
  RiCloseFill,
  RiCloseLine,
  RiDeleteBinLine,
  RiDraftLine,
  RiEditFill,
  RiFileListLine,
  RiFolderMusicLine,
  RiQuestionLine, RiShare2Line, RiUserVoiceLine
} from "react-icons/ri";
import Api from "../../components/helpers/Api";
import {useParams} from "react-router-dom";
import {RiArrowDownLine, RiArrowUpLine, RiDeleteBinFill} from "@remixicon/react";
import classnames from "classnames";
import {
  emailUserPlaylist,
  updatePlaylist as updatePlaylistApi
} from "../../components/helpers/CuratedPlaylistHelpers";
import { adminUpdateSongFlowPublishing } from "../../components/helpers/PublicCuratedSongFlowHelpers";
import { fetchAvailableTemplates } from "../../components/helpers/UserTemplateHelpers";
import withNavigateHook from "../../hooks/withNavigationHook";
import {sessionEnded} from "./SessionEnded";
import AutoCompleteInput from "../song/AutoCompleteInput";
import SeoNoIndex from "../../components/helpers/SeoNoIndex";
import KstTrackLinks from "../../components/kst/KstTrackLinks";
import ServicePartCard from "../../components/service-plan/ServicePartCard";
import SaveAsTemplateModal from "../../components/service-plan/SaveAsTemplateModal";

function AdminToggle({ checked, disabled, onChange, label }) {
    return (
        <button
            type="button"
            onClick={() => !disabled && onChange(!checked)}
            style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '7px',
                background: 'none',
                border: 'none',
                padding: 0,
                cursor: disabled ? 'not-allowed' : 'pointer',
                opacity: disabled ? 0.5 : 1,
                fontSize: '0.875rem',
                color: '#32325d',
            }}
        >
            <span style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '16px',
                height: '16px',
                border: `2px solid ${checked ? '#5e72e4' : '#adb5bd'}`,
                borderRadius: '3px',
                background: checked ? '#5e72e4' : '#fff',
                flexShrink: 0,
                color: '#fff',
                fontSize: '11px',
                lineHeight: 1,
            }}>
                {checked ? '✓' : ''}
            </span>
            {label}
        </button>
    );
}

class YourPlaylist extends React.Component {
  state = {
    isLoading: false,
    listItems: [],
    songBookCount: null,
    playlist: null,
    wordDetails: null,
    saveNotes: null,
    yourName: '',         // NEW: Sender's name
    recipients: [{ name: '', email: '', emailError: '' }],
    removedStack: [],
    isSharing: false,
    showTemplateModal: false,
    showSaveAsTemplateModal: false,
    availableTemplates: { tWBTemplates: [], myTemplates: [], accountTemplates: [] },
    servicePlanTemplates: [],
    templatesLoaded: false,
    pendingTemplate: null,
    shareableCuratedFlow: false,
    published: false,
    publicSlug: null,
    publishingUpdating: false,
  };

  constructor(props) {
    super(props);
    getStats.call(this);

    // sessionStorage.removeItem("customer");
  }

  toggleModal = (state) => {
    this.setState({
      [state]: !this.state[state],
    });
  };

  resetRecipients = () => {
    this.setState({recipients: [{ name: '', email: '', emailError: '' }]});
  };

  openShareModal = () => {
    this.resetRecipients();
    this.toggleModal("formModalDelete");
  };

  componentDidMount() {
    if (sessionStorage.getItem("customer")) {
      const {id} = this.props.params;

      // If there are no songs in the curated-songs list, or we are in saved mode, load from api
      if ((!sessionStorage.getItem("curated-songs") || JSON.parse(sessionStorage.getItem("curated-songs")).length == 0) ||
          (this.isWorkingOnDraft().toLowerCase() === "saved")) {
        this.setState({ isLoading: true });
        Api.get(`/user/` + JSON.parse(sessionStorage.getItem("customer")).user.id + `/curated-playlist/` + id)
            .then(res => {
              this.setState(
                  { playlist: res.data, listItems: this.normaliseListItems(res.data.curatedSongs || []) },
                  this.loadWordDetails
              )
              sessionStorage.setItem("playlist", JSON.stringify(res.data));
              sessionStorage.setItem("curated-songs", JSON.stringify(this.normaliseListItems(res.data.curatedSongs || [])));
              this.setState({listItems: this.normaliseListItems(res.data.curatedSongs || [])});
              this.setState({isLoading: false});
              this.setState({saveNotes: res.data.notes});
              this.setState({
                shareableCuratedFlow: res.data.shareableCuratedFlow || false,
                published: res.data.published || false,
                publicSlug: res.data.publicSlug || null,
              });
              sessionStorage.setItem("workingOnDraft", "false");
              if ((res.data.curatedSongs || []).length === 0 && sessionStorage.getItem("workingOnDraft") !== "true") {
                this.openTemplateModal();
              }
            })
            .catch(err => {
              console.error("Error loading playlist:", err);
              this.setState({ isLoading: false });
            });
      } else {
        // Already have songs or we're in draft mode
        const savedPlaylist = JSON.parse(sessionStorage.getItem("playlist"));
        this.setState({
          playlist: savedPlaylist,
          shareableCuratedFlow: savedPlaylist?.shareableCuratedFlow || false,
          published: savedPlaylist?.published || false,
          publicSlug: savedPlaylist?.publicSlug || null,
        });
        const draftItems = this.normaliseListItems(JSON.parse(sessionStorage.getItem("curated-songs")) || []);
        this.setState({listItems: draftItems});
        this.setState({isLoading: false});
        if (draftItems.length === 0 && sessionStorage.getItem("workingOnDraft") !== "true") {
          this.openTemplateModal();
        }
        // if (JSON.parse(sessionStorage.getItem("playlist")).notes) {
        //   this.setState({saveNotes: JSON.parse(sessionStorage.getItem("playlist")).notes});
        // }
      }
    }
  }

  loadWordDetails = () => {
    // Extract all songId values, remove duplicates and nulls
    const ids = Array.from(
        new Set(
            this.state.listItems
                .filter(item => item.itemType !== 'SERVICE_PART')
                .map(item => item.songId)
                .filter(id => id != null)
        )
    );

    if (ids.length === 0) {
      return;
    }

    Api.post("/words/bulk-detail", ids)
        .then(res => {
          this.setState({ wordDetails: res.data });
        })
        .catch(err => {
          console.error("Could not load word details", err);
        });
  };

  normaliseListItems = (items) => {
    return (items || []).map((item, index) => ({
      ...item,
      itemType: item.itemType || 'SONG',
      i: item.i ?? item.playOrder ?? index,
      playOrder: item.playOrder ?? item.i ?? index
    }));
  };

  trackEvent = (eventName, params = {}) => {
    if (typeof window.gtag === 'function') {
      window.gtag('event', eventName, params);
    }
  };

  openTemplateModal = () => {
    this.setState({ showTemplateModal: true }, this.loadServicePlanTemplates);
  };

  closeTemplateModal = () => {
    this.setState({ showTemplateModal: false, pendingTemplate: null });
    sessionStorage.setItem("workingOnDraft", "true");
  };

  loadServicePlanTemplates = () => {
    if (this.state.templatesLoaded) {
      return;
    }
    fetchAvailableTemplates().then(templates => {
      const availableTemplates = templates || { tWBTemplates: [], myTemplates: [], accountTemplates: [] };
      this.setState({
        availableTemplates,
        servicePlanTemplates: [
          ...(availableTemplates.accountTemplates || []),
          ...(availableTemplates.myTemplates || []),
          ...(availableTemplates.tWBTemplates || [])
        ],
        templatesLoaded: true
      });
    });
  };

  selectTemplate = (template) => {
    this.trackEvent('service_plan_template_selected', { template_id: template.id });
    if (this.state.listItems.length > 0) {
      this.setState({ pendingTemplate: template });
      return;
    }
    this.applyTemplate(template);
  };

  applyTemplate = (template) => {
    const startIndex = this.state.listItems.length;
    const timestamp = Date.now();
    const newTemplateItems = (template.items || []).map((item, index) => {
      const playOrder = startIndex + index;
      if (item.itemType === 'SONG') {
        return {
          i: playOrder,
          playOrder,
          key: item.wordsId + '/' + item.songBookId,
          itemType: 'SONG',
          songId: item.wordsId,
          songTitle: item.wordsTitle,
          songBookId: item.songBookId,
          songBookTitle: item.songBookTitle,
          songBookSongNumber: item.songBookSongNumber
        };
      }
      return {
        i: playOrder,
        playOrder,
        key: 'service-part/' + timestamp + '-' + index,
        itemType: 'SERVICE_PART',
        servicePartTitle: item.servicePartTitle ?? item.title,
        servicePartNotes: item.servicePartNotes ?? item.notes ?? ''
      };
    });

    const listItems = [
      ...this.state.listItems,
      ...newTemplateItems
    ].map((item, i) => ({ ...item, i, playOrder: i }));

    sessionStorage.setItem("curated-songs", JSON.stringify(listItems));
    sessionStorage.setItem("workingOnDraft", "true");
    this.trackEvent('service_plan_template_applied', {
      template_id: template.id,
      item_count: newTemplateItems.length
    });
    const newStackEntries = listItems
      .slice(startIndex)
      .map((item) => ({ type: 'add', item }));
    this.setState((prev) => ({
      listItems,
      showTemplateModal: false,
      pendingTemplate: null,
      removedStack: [...prev.removedStack, ...newStackEntries]
    }));
  };

  renderTemplateGroup = (label, templates) => {
    if (!templates || templates.length === 0) {
      return null;
    }
    return (
        <div className="mb-3">
          <h3 className="text-primary text-uppercase" style={{fontSize: '0.75rem'}}>{label}</h3>
          <div className="service-plan-template-grid">
            {templates.map(template => (
                <Button
                    key={template.id}
                    className="service-plan-template-button"
                    color={(template.items || []).length === 0 ? 'secondary' : 'primary'}
                    type="button"
                    onClick={() => this.selectTemplate(template)}
                >
                  <strong>{template.name}</strong>
                  {template.description && (
                      <>
                        <br />
                        <small>{template.description}</small>
                      </>
                  )}
                  <br />
                  <span>{(template.items || []).length} items</span>
                </Button>
            ))}
          </div>
        </div>
    );
  };

  addServicePart = () => {
    const listItems = [
      ...this.state.listItems,
      {
        i: this.state.listItems.length,
        playOrder: this.state.listItems.length,
        key: 'service-part/' + Date.now(),
        itemType: 'SERVICE_PART',
        servicePartTitle: 'Service Element',
        servicePartNotes: ''
      }
    ].map((item, i) => ({ ...item, i, playOrder: i }));

    sessionStorage.setItem("curated-songs", JSON.stringify(listItems));
    sessionStorage.setItem("workingOnDraft", "true");
    this.trackEvent('service_part_added');
    this.setState({ listItems });
  };

  moveItem = (key, direction) => {
    const listItems = [...this.state.listItems];
    const index = listItems.findIndex(item => item.key === key);
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= listItems.length) return;
    [listItems[index], listItems[targetIndex]] = [listItems[targetIndex], listItems[index]];
    const recalibrated = listItems.map((item, i) => ({ ...item, i, playOrder: i }));
    sessionStorage.setItem("curated-songs", JSON.stringify(recalibrated));
    sessionStorage.setItem("workingOnDraft", "true");
    this.setState({ listItems: recalibrated });
  };

  updateServicePartItem = (key, fields) => {
    const listItems = this.state.listItems.map(item =>
        item.key === key ? { ...item, ...fields } : item
    );
    sessionStorage.setItem("curated-songs", JSON.stringify(listItems));
    sessionStorage.setItem("workingOnDraft", "true");
    this.setState({ listItems });
  };

  handleDragStart = (e, item) => {
    this.setState({ draggingItem: item });
    e.dataTransfer.setData('text/plain', '');
  };

  handleDragEnd = () => {
    this.setState({ draggingItem: null });
    sessionStorage.setItem("workingOnDraft", "true");
  };

  handleDragOver = (e) => {
    e.preventDefault();
  };

  handleDrop = (e, targetItem) => {
    const { draggingItem, listItems } = this.state;
    if (!draggingItem) return;

    const currentIndex = listItems.indexOf(draggingItem);
    const targetIndex = listItems.indexOf(targetItem);

    if (currentIndex !== -1 && targetIndex !== -1) {
      listItems.splice(currentIndex, 1);
      listItems.splice(targetIndex, 0, draggingItem);
      this.setState({ listItems });
      if (draggingItem.itemType === 'SERVICE_PART' || targetItem.itemType === 'SERVICE_PART') {
        this.trackEvent('service_part_reordered');
      }
    }

    // Reassign `i` for ordering on curated song list
    var recalibratedItems = [];
    var i=0;
    for (i=0; i<listItems.length; i++) {
      const current = listItems[i];
      current.i = i;
      current.playOrder = i;
      recalibratedItems.push(current);
    }
    sessionStorage.setItem("curated-songs", JSON.stringify(recalibratedItems));

    sessionStorage.setItem("workingOnDraft", "true");
  };

  removeFromCuratedList = (key) => {
    this.setState((prev) => {
      const index = prev.listItems.findIndex(item => item.key === key);
      if (index === -1) return null;

      const removedItem = prev.listItems[index];
      if (removedItem.itemType === 'SERVICE_PART') {
        this.trackEvent('service_part_removed');
      }

      const newItems = [...prev.listItems];
      newItems.splice(index, 1);

      // Reassign order
      const recalibratedItems = newItems.map((item, i) => ({
        ...item,
        i,
        playOrder: i
      }));

      sessionStorage.setItem("curated-songs", JSON.stringify(recalibratedItems));
      sessionStorage.setItem("workingOnDraft", "true");

      return {
        listItems: recalibratedItems,
        removedStack: [...prev.removedStack, { item: removedItem, index }]
      };
    });
  };

  undoLastRemoval = () => {
    this.setState((prev) => {
      if (prev.removedStack.length === 0) return null;

      const stack = [...prev.removedStack];
      const last = stack.pop(); // LIFO

      let recalibratedItems;
      if (last.type === 'add') {
        // Undo a template addition — remove the item by key
        recalibratedItems = prev.listItems
          .filter(item => item.key !== last.item.key)
          .map((item, i) => ({ ...item, i, playOrder: i }));
      } else {
        // Undo a removal — splice the item back in at its original index
        const newItems = [...prev.listItems];
        newItems.splice(last.index, 0, last.item);
        recalibratedItems = newItems.map((item, i) => ({ ...item, i, playOrder: i }));
      }

      sessionStorage.setItem("curated-songs", JSON.stringify(recalibratedItems));
      sessionStorage.setItem("workingOnDraft", "true");

      return {
        listItems: recalibratedItems,
        removedStack: stack
      };
    });
  };

  renderScriptureChoosePassages = () => {
    sessionStorage.setItem("keepPlaylist", true);
    return "/scripture/choose-passages";
  }

  renderThemesChoosePassages = () => {
    return "/themes/choose-themes";
  }

  updatePlaylist = () => {
    sessionStorage.setItem("curated-songs", JSON.stringify(this.state.listItems));
    // updatePlaylist(this.props.params.id, this.props.params.name, this.state.saveNotes, JSON.parse(sessionStorage.getItem("curated-songs")));
    updatePlaylistApi(this.props.params.id, this.props.params.name, this.state.saveNotes, this.state.listItems).then(result => {
      if (result) {
        const listItems = this.state.listItems;
        this.trackEvent('service_plan_saved', {
          item_count: listItems.length,
          song_count: listItems.filter(i => i.itemType !== 'SERVICE_PART').length,
          service_part_count: listItems.filter(i => i.itemType === 'SERVICE_PART').length
        });
        this.setState({
          emailError: "* ",
          formSubmitted: false
        })
      } else {
        this.setState({
          generalError: "Something went wrong, if this persists please contact us",
          formSubmitted: false
        })
      }

      const playlist = JSON.parse(sessionStorage.getItem("playlist"));
      playlist.notes = this.state.saveNotes;
      sessionStorage.setItem("playlist", JSON.stringify(playlist));

      this.setState({saveFormSubmitted:  true});
      this.props.navigation(0);

      sessionStorage.removeItem("workingOnDraft");

      sessionStorage.removeItem("curated-songs")
      // sessionStorage.removeItem("playlist");
    });
  }

  resetPlaylist = () => {
    sessionStorage.removeItem("playlist");

    sessionStorage.removeItem("curated-songs");
    sessionStorage.removeItem("workingOnDraft");

    this.props.navigation(0);
  }

  onChangeSaveName = (e) => {
    this.setState({
      saveName: e.target.value,
      saveNameError: "",
    });
  };

  onChangeSaveNotes = (e) => {
    this.setState({
      saveNotes: e.target.value,
      saveNotesError: "",
    });

    sessionStorage.setItem("workingOnDraft", "true");
  };

  displayListItem = () => {
    const { wordDetails } = this.state;
    return !this.state.listItems
        ? []
        : this.state.listItems.map((item, index) => {
            const isFirst = index === 0;
            const isLast = index === this.state.listItems.length - 1;
            if (item.itemType === 'SERVICE_PART') {
              return (
                  <ServicePartCard
                      key={item.key}
                      item={item}
                      onRemove={this.removeFromCuratedList}
                      onUpdate={this.updateServicePartItem}
                      onMoveUp={isFirst ? null : () => this.moveItem(item.key, -1)}
                      onMoveDown={isLast ? null : () => this.moveItem(item.key, 1)}
                      isDragging={this.state.draggingItem?.key === item.key}
                      dragHandlers={{
                        onDragStart: (e) => this.handleDragStart(e, item),
                        onDragEnd: this.handleDragEnd,
                        onDragOver: this.handleDragOver,
                        onDrop: (e) => this.handleDrop(e, item)
                      }}
                  />
              );
            }
            // Look up WSB metadata from bulk-loaded word details
            const wordDetail = wordDetails && wordDetails.find(wd => wd.id === item.songId);
            const wsbDetail = wordDetail && wordDetail.wordsSongBooks &&
                wordDetail.wordsSongBooks.find(wsb => wsb.songBook && wsb.songBook.id === item.songBookId);
            const metaParts = [];
            const keySig  = item.keySignature  || wsbDetail?.keySignature;
            const timeSig  = item.timeSignature || wsbDetail?.timeSignature;
            const tempo    = item.tempoBpm      ?? wsbDetail?.tempoBpm;
            const meter    = item.meter         || wsbDetail?.meter;
            const ccli     = item.ccliNumber    || wsbDetail?.ccliNumber;
            const kstTracks = item.kstTracks || wordDetail?.kstTracks || [];
            if (keySig)  metaParts.push(`Key: ${keySig}`);
            if (timeSig) metaParts.push(`Time Signature: ${timeSig}`);
            if (tempo)   metaParts.push(`Temp: ${tempo} bpm`);
            if (meter)   metaParts.push(`Metre: ${meter}`);
            if (ccli)    metaParts.push(`CCLI: ${ccli}`);

            return (
                <div
                    key={item.key}
                    className={`item ${item === this.state.draggingItem ? "dragging" : ""}`}
                    draggable="true"
                    onDragStart={(e) => this.handleDragStart(e, item)}
                    onDragEnd={this.handleDragEnd}
                    onDragOver={this.handleDragOver}
                    onDrop={(e) => this.handleDrop(e, item)}
                >
                  <div className="align-items-start col-md-12 col-xs-12 row">
                    {/* Title */}
                    <div className="col-md-6 col-xs-12" style={{ fontWeight: "bold" }}>
                      {item.songTitle}
                      <KstTrackLinks kstTracks={kstTracks} />

                      {/* Authors */}
                      {item.authors && item.authors.length > 0 && (
                          <div
                              style={{
                                fontWeight: "normal",
                                fontSize: "0.85rem",
                                color: "#555",
                                marginTop: "0.25rem",
                              }}
                          >
                            {item.authors.map(a => a.name).join(", ")}
                          </div>
                      )}
                    </div>

                    {/* SongBook info */}
                    <div className="col-md-4 col-xs-12">
                      {item.songBookTitle}&nbsp;&nbsp;({item.songBookSongNumber})
                      {metaParts.length > 0 && (
                          <div style={{ fontSize: "0.75rem", color: "#888" }}>
                            {metaParts.join(' · ')}
                          </div>
                      )}
                    </div>

                    {/* Controls */}
                    <div className="col-md-2 col-xs-12">
                      <Button
                          color="link"
                          className="p-0"
                          onClick={() => this.removeFromCuratedList(item.key)}
                      >
                        <RiDeleteBinFill color="grey" />
                      </Button>
                      &nbsp;
                      <Button color="link" className="p-0" disabled={isFirst} onClick={() => this.moveItem(item.key, -1)} title="Move up">
                        <RiArrowUpLine color={isFirst ? "#ccc" : "grey"} />
                      </Button>
                      <Button color="link" className="p-0" disabled={isLast} onClick={() => this.moveItem(item.key, 1)} title="Move down">
                        <RiArrowDownLine color={isLast ? "#ccc" : "grey"} />
                      </Button>
                    </div>
                  </div>
                </div>
            );
        });
  };

  sharePlaylist = () => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const recipients = this.state.recipients.map(recipient => ({
      ...recipient,
      emailError: emailRegex.test(recipient.email) ? '' : 'Please enter a valid email address'
    }));
    const hasErrors = recipients.some(recipient => recipient.emailError);

    if (hasErrors) {
      this.setState({recipients});
      return;
    }

    this.setState({ isSharing: true });
    emailUserPlaylist(
        this.state.playlist.id,
        JSON.parse(sessionStorage.getItem("customer")).user.firstName,
        this.state.recipients.map(r => ({ name: r.name, email: r.email }))
    ).then(result => {
      if (result) {
        this.setState({ isSharing: "done" });
        setTimeout(() => {
          // sessionStorage.setItem("page-confirmation", "Your " + this.state.playlist.name + " playlist has been sent");
          this.setState({ isSharing: false, recipients: [{ name: '', email: '', emailError: '' }] });
          this.toggleModal("formModalDelete");
          this.toggleModal("formModalSent");
          // this.props.navigation('/playlist/' + this.state.playlist.id + '/' + this.state.playlist.name);
        }, 800);
      } else {
        // sessionStorage.setItem("page-error", "An error occurred sending your " + this.state.playlist.name + " playlist");
        // this.toggleModal("formModalDelete");
        this.setState({ isSharing: false });
        this.toggleModal("formModalSendError");
        // this.props.navigation('/playlist/' + this.state.playlist.id + '/' + this.state.playlist.name);
      }
    });
  }

  // New change handlers for the additional input fields
  onChangeYourName = (e) => {
    this.setState({ yourName: e.target.value });
  };

  onChangeRecipientName = (index, value) => {
    const recipients = [...this.state.recipients];
    recipients[index] = {...recipients[index], name: value};
    this.setState({recipients});
  };

  onChangeRecipientEmail = (index, value) => {
    const recipients = [...this.state.recipients];
    recipients[index] = {...recipients[index], email: value, emailError: ''};
    this.setState({recipients});
  };

  addRecipient = () => {
    this.setState({
      recipients: [...this.state.recipients, { name: '', email: '', emailError: '' }]
    });
  };

  removeRecipient = (index) => {
    if (this.state.recipients.length <= 1) {
      return;
    }

    this.setState({
      recipients: this.state.recipients.filter((recipient, recipientIndex) => recipientIndex !== index)
    });
  };

  delete = (id) => {
    Api.delete(`/user/` + JSON.parse(sessionStorage.getItem("customer")).user.id + `/curated-playlist/` + id)
        .then(res => {
          this.props.navigation("/your-profile/saved-song-flows");
        });
  }

  rename = (id) => {
    const request = {
      name: this.state.saveNewName
    }
    Api.put(`/user/` + JSON.parse(sessionStorage.getItem("customer")).user.id + `/curated-playlist/` + id, request)
        .then(res => {
          if (res.status === 200) {
            this.state.playlist = res.data;
            this.props.navigation(0);
          } else {
            this.newNameError = "There was a problem renaming the plan, please choose an alternative name that hasn't been used before"
            this.props.navigation("/your-profile/saved-song-flows");
          }
        });
  }

  isModelOpen = (name) => {
    return this.state[name];
  }

  onChangeSaveNewName = (e) => {
    this.setState({
      saveNewName: e.target.value,
      saveNewNameError: "",
    });
  };

  isAdmin = () => {
    const customer = JSON.parse(sessionStorage.getItem("customer") || "null");
    return customer?.user?.isAdmin === true;
  };

  updatePublishing = (shareableCuratedFlow, published) => {
    this.setState({ publishingUpdating: true });
    adminUpdateSongFlowPublishing(this.getPlaylistId(), shareableCuratedFlow, published)
        .then(result => {
          if (result) {
            this.setState({
              shareableCuratedFlow: result.shareableCuratedFlow || false,
              published: result.published || false,
              publicSlug: result.publicSlug || null,
            });
          }
          this.setState({ publishingUpdating: false });
        });
  };

  getPlaylistId = () => {
    return this.state.playlist ? this.state.playlist.id : 'xx';
  }

  getPlaylistName = () => {
    return this.state.playlist ? this.state.playlist.name : 'Not set';
  }

  isWorkingOnDraft() {
    return sessionStorage.getItem("workingOnDraft") && sessionStorage.getItem("workingOnDraft").toLowerCase() === "true" ? "Draft" : "Saved";
  }

  render() {
    if (!sessionStorage.getItem("customer")) {
      return sessionEnded();
    }

    // Once wordDetails is loaded, render the cloud:
    const { wordDetails } = this.state;

    const playlistId = this.getPlaylistId();
    const playlistName = this.getPlaylistName();
    const isDraft = this.isWorkingOnDraft().toLowerCase() === "draft";

    return (
      <>
        <SeoNoIndex noindex={true} />
        <Modal
            className="modal-dialog-centered"
            size="lg"
            isOpen={this.state.showTemplateModal}
            toggle={this.closeTemplateModal}
        >
          <div className="modal-header">
            <h2 style={{fontSize: '1rem'}} className="modal-title" id="service-plan-template-modal">
              Choose a Service Plan template
            </h2>
            <button
                aria-label="Close"
                className="close"
                data-dismiss="modal"
                type="button"
                onClick={this.closeTemplateModal}
            >
              <span aria-hidden={true}>×</span>
            </button>
          </div>
          <div className="modal-body">
            {this.state.pendingTemplate ? (
                <>
                  <p>
                    Applying <strong>{this.state.pendingTemplate.name}</strong> will add these items to your current service plan.
                    Existing songs and service elements will not be removed.
                  </p>
                  <div className="d-flex justify-content-end">
                    <Button
                        color="secondary"
                        type="button"
                        onClick={() => this.setState({ pendingTemplate: null })}
                    >
                      Cancel
                    </Button>
                    <Button
                        color="primary"
                        type="button"
                        onClick={() => this.applyTemplate(this.state.pendingTemplate)}
                    >
                      Apply Template
                    </Button>
                  </div>
                </>
            ) : (
                <>
                  {this.renderTemplateGroup("Church Templates", this.state.availableTemplates.accountTemplates)}
                  {this.renderTemplateGroup("My Templates", this.state.availableTemplates.myTemplates)}
                  {this.renderTemplateGroup("TWB Templates", this.state.availableTemplates.tWBTemplates)}
                </>
            )}
          </div>
        </Modal>
        <SaveAsTemplateModal
            isOpen={this.state.showSaveAsTemplateModal}
            toggle={() => this.setState({ showSaveAsTemplateModal: false })}
            playlistId={this.props.params.id}
            playlistName={this.state.playlist?.name}
            onSaved={() => {
              this.setState({ showSaveAsTemplateModal: false, templatesLoaded: false });
              this.trackEvent('service_plan_saved_as_template');
            }}
        />

        {/*<main ref="main">*/}
          <div className="position-relative">
            {/* shape Hero */}
            <section className="section section-lg section-shaped pb-250">
              <div className="shape shape-style-1 shape-default">
                <span/>
                <span/>
                <span/>
                <span/>
                <span/>
                <span/>
                <span/>
                <span/>
                <span/>
              </div>
              <Container className="py-lg-md d-flex">
                <div className="col px-0">
                  <Row>
                    <Col lg="8">
                      <h1 className="display-3 text-white">
                        Service Plan{" "}
                        <span>{playlistName}</span>
                      </h1>
                    </Col>
                  </Row>
                </div>
              </Container>
              {/* SVG separator */}
              <div className="separator separator-bottom separator-skew">
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    preserveAspectRatio="none"
                    version="1.1"
                    viewBox="0 0 2560 100"
                    x="0"
                    y="0"
                >
                  <polygon
                      className="fill-white"
                      points="2560 0 2560 100 0 100"
                  />
                </svg>
              </div>
            </section>
            {/* 1st Hero Variation */}
          </div>
          <section className="section section-lg pt-lg-0 mt--200">
            <Container>
              <Row className="justify-content-center">
                <Col lg="12">
                  <Row className="row-grid">
                    <Col lg="12">
                      <Card className="card-lift--hover shadow border-0">
                        <CardBody className="py-5">
                          {/*<div className="icon icon-shape icon-shape-primary rounded-circle mb-4">*/}
                          {/*  <i className="ni ni-check-bold" />*/}
                          {/*</div>*/}
                          <Row>
                            <Col>
                              <span className="text-primary text-uppercase">
                                <RiFolderMusicLine color='#5e72e4' size="36"/>&nbsp;<b>{playlistName}</b>
                              </span>
                              <a href="#" className="ml-3" onClick={() => this.toggleModal("formModalRename" + playlistId)}
                                 title="Rename">
                                <RiEditFill title="Rename" size="20"/>
                              </a>
                              <a href="#" className="ml-3" onClick={() => this.toggleModal("formModalDelete" + playlistId)}
                                 title="Delete">
                                <RiDeleteBinFill title="Delete" size="20"/>
                              </a>
                              <span className="text-primary text-uppercase description ml-3">{isDraft ?
                                  <>
                                    <Button
                                        className="btn-tooltip"
                                        color="neutral"
                                        id="tooltipDraft"
                                        size="sm"
                                    >
                                      draft&nbsp;<RiQuestionLine/>
                                    </Button>
                                    <UncontrolledTooltip
                                        delay={0}
                                        placement="right"
                                        target="tooltipDraft"
                                        trigger="hover focus"
                                    >
                                      Save changes to keep this plan up to date
                                    </UncontrolledTooltip>
                                  </> :
                                  <>
                                    <Button
                                        className="btn-tooltip"
                                        color="neutral"
                                        id="tooltipSaved"
                                        size="sm"
                                    >
                                      Saved&nbsp;<RiQuestionLine/>
                                    </Button>
                                    <UncontrolledTooltip
                                        delay={0}
                                        placement="right"
                                        target="tooltipSaved"
                                        trigger="hover focus"
                                    >
                                      Updates to this plan have been saved
                                    </UncontrolledTooltip>
                                  </>}
                              </span>
                              <Modal
                                  className="modal-dialog-centered"
                                  size="sm"
                                  isOpen={this.isModelOpen("formModalRename" + playlistId)}
                                  toggle={() => this.toggleModal("formModalRename" + playlistId)}
                              >
                                <div className="modal-header">
                                  <h2 style={{fontSize: '1rem'}} className="modal-title" id="modal-title-default">
                                    Rename your service plan {playlistName}
                                  </h2>
                                  <button
                                      aria-label="Close"
                                      className="close"
                                      data-dismiss="modal"
                                      type="button"
                                      onClick={() => this.toggleModal("formModalRename" + playlistId)}
                                  >
                                    <span aria-hidden={true}>×</span>
                                  </button>
                                </div>
                                <div className="modal-body p-0">
                                  <Card className="bg-secondary shadow border-0">
                                    <CardBody className="px-lg-5 py-lg-5">
                                      <Form role="form">
                                        <FormGroup
                                            className={classnames("mb-3", {
                                              focused: this.state.saveNameFocused,
                                            })}
                                        >
                                          <InputGroup className="input-group-alternative">
                                            {/*<InputGroupAddon addonType="prepend">*/}
                                            <InputGroupText>
                                              <RiDraftLine/>
                                            </InputGroupText>
                                            {/*</InputGroupAddon>*/}
                                            <Input
                                                placeholder="New name"
                                                type="text"
                                                onFocus={(e) =>
                                                    this.setState({saveNewNameFocused: true})
                                                }
                                                onBlur={(e) =>
                                                    this.setState({saveNewNameFocused: false})
                                                }
                                                onChange={this.onChangeSaveNewName}
                                            />
                                          </InputGroup>
                                        </FormGroup>
                                        <div className="text-center">
                                          <Button
                                              className="my-4"
                                              color="neutral"
                                              type="button"
                                              onClick={() => this.toggleModal("formModalRename" + playlistId)}
                                          >
                                            <RiCloseFill title="Close" size="25"/>
                                            <span className="btn-inner--text">Cancel</span>
                                          </Button>
                                          <Button
                                              className="my-4"
                                              color="primary"
                                              type="button"
                                              disabled={this.newName || this.newName === playlistName}
                                              onClick={() => this.rename(playlistId)}
                                          >
                                            <RiEditFill title="Delete" size="25"/>
                                            <span className="btn-inner--text">Rename</span>
                                          </Button>
                                        </div>
                                      </Form>
                                    </CardBody>
                                  </Card>
                                </div>
                              </Modal>
                              <Modal
                                  className="modal-dialog-centered"
                                  size="sm"
                                  isOpen={this.isModelOpen("formModalDelete" + playlistId)}
                                  toggle={() => this.toggleModal("formModalDelete" + playlistId)}
                              >
                                <div className="modal-header">
                                  <h2 style={{fontSize: '1rem'}} className="modal-title" id="modal-title-default">
                                    Delete your service plan {playlistName}
                                  </h2>
                                  <button
                                      aria-label="Close"
                                      className="close"
                                      data-dismiss="modal"
                                      type="button"
                                      onClick={() => this.toggleModal("formModalDelete" + playlistId)}
                                  >
                                    <span aria-hidden={true}>×</span>
                                  </button>
                                </div>
                                <div className="modal-body p-0">
                                  <Card className="bg-secondary shadow border-0">
                                    <CardBody className="px-lg-5 py-lg-5">
                                      <Form role="form">
                                        <div className="text-center">
                                          <Button
                                              className="my-4"
                                              color="neutral"
                                              type="button"
                                              onClick={() => this.toggleModal("formModalDelete" + playlistId)}
                                          >
                                            <RiCloseFill title="Close" size="25"/>
                                            <span className="btn-inner--text">Cancel</span>
                                          </Button>
                                          <Button
                                              className="my-4"
                                              color="warning"
                                              type="button"
                                              onClick={() => this.delete(playlistId)}
                                          >
                                            <RiDeleteBinLine title="Delete" size="25"/>
                                            <span className="btn-inner--text">Delete</span>
                                          </Button>
                                        </div>
                                      </Form>
                                    </CardBody>
                                  </Card>
                                </div>
                              </Modal>
                            </Col>
                            <Col className="text-right">
                              {this.state.removedStack.length > 0 && (
                                  <Button
                                      className="btn-icon btn-sm ml-3"
                                      color="warning"
                                      type="button"
                                      onClick={this.undoLastRemoval}
                                  >
                                    <span className="btn-inner--icon">
                                      <i className="ni ni-undo" />
                                    </span>
                                    <span className="btn-inner--text">
                                      Undo ({this.state.removedStack.length})
                                    </span>
                                  </Button>
                              )}
                            </Col>
                          </Row>
                          <p className="description mt-3">
                            <FormGroup
                                className={classnames("mb-3", {
                                  focused: this.state.saveNotesFocused,
                                })}
                            >
                              <InputGroup className="input-group-alternative">
                                {/*<InputGroupAddon addonType="prepend">*/}
                                <InputGroupText>
                                  <RiDraftLine/>
                                </InputGroupText>
                                {/*</InputGroupAddon>*/}
                                <Input
                                    placeholder="Type your notes here"
                                    type="textarea"
                                    defaultValue={this.state.playlist ? this.state.playlist.notes : ''}
                                    onFocus={(e) =>
                                        this.setState({saveNotesFocused: true})
                                    }
                                    onBlur={(e) =>
                                        this.setState({saveNotesFocused: false})
                                    }
                                    onChange={this.onChangeSaveNotes}
                                />
                              </InputGroup>
                            </FormGroup>
                          </p>
                          <div className="mb-3">
                            <Button
                                block
                                className="mb-3"
                                color="primary"
                                type="button"
                                onClick={this.openTemplateModal}
                            >
                              <RiFileListLine size={18} />
                              <span className="btn-inner--text">Choose service template</span>
                            </Button>
                            <Button
                                block
                                className="mb-3"
                                color="secondary"
                                type="button"
                                onClick={this.addServicePart}
                            >
                              <RiAddLine size={18} />
                              <span className="btn-inner--text">Add service element</span>
                            </Button>
                            <Button
                                block
                                className="mb-3"
                                color="secondary"
                                type="button"
                                onClick={() => this.setState({ showSaveAsTemplateModal: true })}
                            >
                              <RiFileListLine size={18} />
                              <span className="btn-inner--text">Save as Template</span>
                            </Button>
                          </div>
                          <div className="sortable-list" style={{width: '100%'}}>
                            {this.state.isLoading ? (
                              <div className="spinner-container">
                                <Spinner animation="border" role="status">
                                  <span className="visually-hidden"></span>
                                </Spinner>
                                &nbsp;Loading...
                              </div>
                            ) : this.displayListItem()}
                          </div>
                        </CardBody>
                      </Card>
                    </Col>
                  </Row>
                  <Row>
                    &nbsp;
                  </Row>

                  {this.isAdmin() && (
                      <Row className="mb-3">
                        <Col lg="12">
                          <Card className="shadow border-0" style={{ borderLeft: '4px solid #5e72e4' }}>
                            <CardBody className="py-3 px-4">
                              <div className="d-flex align-items-center flex-wrap" style={{ gap: '20px' }}>
                                <small className="text-uppercase text-muted font-weight-bold" style={{ minWidth: '50px' }}>Admin</small>
                                <AdminToggle
                                    checked={this.state.shareableCuratedFlow}
                                    disabled={this.state.publishingUpdating || isDraft}
                                    onChange={(v) => this.updatePublishing(v, this.state.published && v)}
                                    label="Allow public subscription"
                                />
                                {this.state.shareableCuratedFlow && (
                                    <AdminToggle
                                        checked={this.state.published}
                                        disabled={this.state.publishingUpdating || isDraft}
                                        onChange={(v) => this.updatePublishing(true, v)}
                                        label="Published (live)"
                                    />
                                )}
                                {this.state.publicSlug && this.state.published && (
                                    <small>
                                      <a href={`/song-flows/${this.state.publicSlug}/subscribe`} target="_blank" rel="noopener noreferrer" style={{ color: '#5e72e4' }}>
                                        /song-flows/{this.state.publicSlug}/subscribe
                                      </a>
                                    </small>
                                )}
                                {isDraft && (
                                    <small className="text-muted">Save changes before updating publishing</small>
                                )}
                              </div>
                            </CardBody>
                          </Card>
                        </Col>
                      </Row>
                  )}

                  <Row className="justify-content-center">
                    <p className="description mt-3 text-warning">
                      {isDraft && <>
                        Save changes before proceeding
                      </>
                      }
                      {!isDraft && <>
                        &nbsp;
                      </>
                      }
                    </p>
                  </Row>

                  <Row className="justify-content-center mb-5">
                    <Col lg="12">
                      <Card className="card-lift--hover shadow border-0">
                        <CardBody className="py-5">
                          <h2 style={{fontSize: '1rem'}} className="text-primary text-uppercase d-flex align-items-center">
                            <span className="icon icon-shape icon-shape-primary rounded-circle">
                              <RiUserVoiceLine/>
                            </span>
                            <span className="ml-3">Add a song by name</span>
                          </h2>
                          <p className="description mt-3">
                            <AutoCompleteInput navTo="/songs/search"/>
                          </p>
                        </CardBody>
                      </Card>
                    </Col>
                  </Row>

                  <Row>
                    <Col md="6">
                      <Button
                          block
                          className="mb-3"
                          color="primary"
                          type="button"
                          disabled={isDraft}
                          href={this.renderScriptureChoosePassages()}
                      >
                        <span className="btn-inner--icon mr-3">
                          <i className="ni ni-book-bookmark"/>
                        </span>
                        Add more from Scripture
                      </Button>
                    </Col>
                    <Col md="6">
                      <Button
                          block
                          className="mb-3"
                          color="primary"
                          type="button"
                          disabled={isDraft}
                          href={this.renderThemesChoosePassages()}
                      >
                        <span className="btn-inner--icon mr-3">
                          <i className="ni ni-archive-2"/>
                        </span>
                        Add more from Theme
                      </Button>
                    </Col>
                    {/*<Col md="4">*/}
                    {/*  <Button*/}
                    {/*      block*/}
                    {/*      className="mb-3"*/}
                    {/*      color="primary"*/}
                    {/*      type="button"*/}
                    {/*      disabled={isDraft}*/}
                    {/*      href="/lectionary/choose-lectionary"*/}
                    {/*  >*/}
                    {/*    <span className="btn-inner--icon mr-3">*/}
                    {/*      <i className="ni ni-books" />*/}
                    {/*    </span>*/}
                    {/*    Add more from Lectionary*/}
                    {/*  </Button>*/}
                    {/*</Col>*/}
                  </Row>

                  <Row>
                    <Col lg="3">
                      <div className="btn-wrapper">
                        {/*<Button*/}
                        {/*    type="submit"*/}
                        {/*    className="btn-icon mb-3 mb-sm-0"*/}
                        {/*    color="secondary"*/}
                        {/*    href={this.backToWhere()}*/}
                        {/*>*/}
                        {/*  <span className="btn-inner--text">Back</span>*/}
                        {/*</Button>*/}
                      </div>
                    </Col>
                    <Col lg="5"/>
                  </Row>
                  <div style={{
                    position: "fixed",
                    bottom: "0",
                    left: "0",
                    width: "100%",
                    backgroundColor: "white",
                    padding: "55px",
                    boxShadow: "0 -2px 5px rgba(0, 0, 0, 0.2)",
                    zIndex: "1000"
                  }}>
                    <div className="col-md-12 col-xs-12"
                         style={{position: "fixed", bottom: "50px", right: "0px", zIndex: "1000"}}>
                      <Button
                          block
                          className="mb-3"
                          color="primary"
                          type="button"
                          onClick={this.updatePlaylist}
                          // disabled={!isDraft || (!this.state.listItems || this.state.listItems.length === 0)}
                      >
                        <RiFolderMusicLine size="24"/>&nbsp;
                        Save changes
                      </Button>
                    </div>
                    <div className="col-md-12 col-xs-12"
                         style={{position: "fixed", bottom: "0px", right: "0px", zIndex: "1000"}}>
                      <Button
                          block
                          className="mb-3"
                          color="primary"
                          type="button"
                          onClick={this.resetPlaylist}
                          disabled={!isDraft}
                      >
                        <RiFolderMusicLine size="24"/>&nbsp;
                        Reset
                      </Button>
                    </div>
                  </div>
                  <Row>
                    <Col md="6">
                      <Button
                          block
                          type="submit"
                          className="mb-3"
                          color="primary"
                          href="/lectionary/choose-lectionary"
                      >
                        <RiShare2Line />
                        <span className="btn-inner--text">Add from Lectionary</span>
                      </Button>
                    </Col>
                    <Col lg="6">
                      <Button
                          block
                          type="submit"
                          className="mb-3"
                          color="neutral"
                          disabled={isDraft}
                          onClick={this.openShareModal}
                          // disabled={!this.continueButtonAvailable()}
                      >
                        <RiShare2Line/>
                        <span className="btn-inner--text">Share with others</span>
                      </Button>
                      <Modal
                          className="modal-dialog-centered"
                          size="md"
                          isOpen={this.state.formModalDelete}
                          toggle={() => this.toggleModal("formModalDelete")}
                      >
                        <div className="modal-header">
                          <h2 style={{fontSize: '1rem'}} className="modal-title" id="modal-title-default">
                            Share your service plan ...
                          </h2>
                          <button
                              aria-label="Close"
                              className="close"
                              data-dismiss="modal"
                              type="button"
                              disabled={isDraft}
                              onClick={() => this.toggleModal("formModalDelete")}
                          >
                            <span aria-hidden={true}>×</span>
                          </button>
                        </div>
                        <div className="modal-body p-0">
                          <Card className="bg-secondary shadow border-0">
                            <CardBody className="px-lg-5 py-lg-5">
                              {!sessionStorage.getItem("customer") && (() => {
                                const used = parseInt(localStorage.getItem("swo") || "0", 10);
                                const remaining = Math.max(0, 3 - used);
                                return (
                                  <p className="text-muted mb-4" style={{ fontSize: "0.85rem" }}>
                                    You have {remaining} of 3 shares remaining. Create an account to keep sharing with your team, plus save and manage your plans without limits.
                                  </p>
                                );
                              })()}
                              <Form role="form">
                                {/* NEW: Input for "Your Name" */}
                                {/*<FormGroup className="mb-3">*/}
                                {/*  <InputGroup className="input-group-alternative">*/}
                                {/*    <InputGroupText>*/}
                                {/*      <i className="ni ni-circle-08" />*/}
                                {/*    </InputGroupText>*/}
                                {/*    <Input*/}
                                {/*        placeholder="Your Name"*/}
                                {/*        type="text"*/}
                                {/*        onChange={this.onChangeYourName}*/}
                                {/*    />*/}
                                {/*  </InputGroup>*/}
                                {/*</FormGroup>*/}
                                <p className="text-muted mb-3" style={{fontSize: '0.85rem'}}>
                                  Add one or more people to share this with
                                </p>
                                {this.state.recipients.map((recipient, index) => (
                                    <div key={index} className="d-flex align-items-start mb-2">
                                      <div style={{flex: 1, marginRight: '8px'}}>
                                        <Input
                                            placeholder="Recipient's Name"
                                            type="text"
                                            value={recipient.name}
                                            onChange={(e) => this.onChangeRecipientName(index, e.target.value)}
                                        />
                                      </div>
                                      <div style={{flex: 1, marginRight: this.state.recipients.length > 1 ? '8px' : '0'}}>
                                        <Input
                                            placeholder="Email"
                                            type="email"
                                            value={recipient.email}
                                            onChange={(e) => this.onChangeRecipientEmail(index, e.target.value)}
                                        />
                                        {recipient.emailError && (
                                            <small className="text-danger">{recipient.emailError}</small>
                                        )}
                                      </div>
                                      {this.state.recipients.length > 1 && (
                                          <div style={{paddingTop: '6px'}}>
                                            <Button
                                                id={"remove-recipient-" + index}
                                                color="link"
                                                type="button"
                                                className="p-0 text-muted"
                                                onClick={() => this.removeRecipient(index)}
                                            >
                                              <RiCloseLine size={16} />
                                            </Button>
                                            <UncontrolledTooltip target={"remove-recipient-" + index} placement="top">
                                              Remove this recipient
                                            </UncontrolledTooltip>
                                          </div>
                                      )}
                                    </div>
                                ))}
                                <div className="mb-3">
                                  <Button
                                      id="add-recipient"
                                      color="link"
                                      type="button"
                                      className="p-0"
                                      onClick={this.addRecipient}
                                  >
                                    <RiAddLine size={14} />
                                    <span style={{marginLeft: '4px'}}>Add another person</span>
                                  </Button>
                                  <UncontrolledTooltip target="add-recipient" placement="top">
                                    Add another recipient
                                  </UncontrolledTooltip>
                                </div>
                                <div className="text-center">
                                  <Button
                                      className="my-4"
                                      color="primary"
                                      type="button"
                                      disabled={this.state.isSharing !== false}
                                      onClick={this.sharePlaylist}
                                  >
                                    <i className="fa fa-share-alt" aria-hidden="true"></i>
                                    <span className="btn-inner--text">
                                      {this.state.isSharing === "done" ? "✓ Shared" : this.state.isSharing ? "Sending…" : "Share plan"}
                                    </span>
                                  </Button>
                                </div>
                              </Form>
                            </CardBody>
                          </Card>
                        </div>
                      </Modal>
                      <Modal
                          className="modal-dialog-centered"
                          size="sm"
                          isOpen={this.state.formModalSent}
                          toggle={() => this.toggleModal("formModalSent")}
                      >
                        <div className="modal-header">
                          <h2 style={{fontSize: '1rem'}} className="modal-title" id="modal-title-default">
                            Share your service plan ...
                          </h2>
                          <button
                              aria-label="Close"
                              className="close"
                              data-dismiss="modal"
                              type="button"
                              onClick={() => this.toggleModal("formModalSent")}
                          >
                            <span aria-hidden={true}>×</span>
                          </button>
                        </div>
                        <div className="modal-body p-0">
                          <Card className="bg-secondary shadow border-0">
                            <CardBody className="px-lg-5 py-lg-5">
                              <div className="text-center text-muted mb-4">
                                Your {this.state.playlist ? this.state.playlist.name : ""} plan was successfully
                                shared
                              </div>
                              <Form role="form">
                                <div className="text-center">
                                  <Button
                                      className="my-4"
                                      color="primary"
                                      type="button"
                                      onClick={() => this.toggleModal("formModalSent")}
                                  >
                                    <i className="fa fa-share-alt" aria-hidden="true"></i>
                                    <span className="btn-inner--text">Close</span>
                                  </Button>
                                </div>
                              </Form>
                            </CardBody>
                          </Card>
                        </div>
                      </Modal>
                      <Modal
                          className="modal-dialog-centered"
                          size="sm"
                          isOpen={this.state.formModalSendError}
                          toggle={() => this.toggleModal("formModalSendError")}
                      >
                        <div className="modal-header">
                          <h2 style={{fontSize: '1rem'}} className="modal-title" id="modal-title-default">
                            Share your service plan ...
                          </h2>
                          <button
                              aria-label="Close"
                              className="close"
                              data-dismiss="modal"
                              type="button"
                              onClick={() => this.toggleModal("formModalSendError")}
                          >
                            <span aria-hidden={true}>×</span>
                          </button>
                        </div>
                        <div className="modal-body p-0">
                          <Card className="bg-secondary shadow border-0">
                            <CardBody className="px-lg-5 py-lg-5">
                              <div className="text-center text-muted mb-4">
                                There was an issue sharing
                                the {this.state.playlist ? this.state.playlist.name : ""} plan, please check the
                                email address and retry
                              </div>
                              <Form role="form">
                                <div className="text-center">
                                  <Button
                                      className="my-4"
                                      color="primary"
                                      type="button"
                                      onClick={() => this.toggleModal("formModalSendError")}
                                  >
                                    <i className="fa fa-share-alt" aria-hidden="true"></i>
                                    <span className="btn-inner--text">Close</span>
                                  </Button>
                                </div>
                              </Form>
                            </CardBody>
                          </Card>
                        </div>
                      </Modal>
                    </Col>
                  </Row>
                </Col>
              </Row>
            </Container>
          </section>
        {/*</main>*/}

        {/*{wordDetails && (*/}
        {/*    <Container className="mb-5 mt--5">*/}
        {/*      <Row>*/}
        {/*        <Col>*/}
        {/*          <h4>A word cloud based on your song-flow songs, themes & scriptures references</h4>*/}
        {/*          <DebouncedWordCloud*/}
        {/*              playlist={wordDetails}*/}
        {/*              style={{ height: "500px" }}*/}
        {/*          />*/}
        {/*        </Col>*/}
        {/*      </Row>*/}
        {/*    </Container>*/}
        {/*)}*/}
      </>
    );
  }
}

export default withNavigateHook((props) => (
    <YourPlaylist{...props} params={useParams()}/>
));
